/**
 * Module landing pages for Quixo OS menu items whose dedicated workspace is still being built (SEO & Visibility, CRM,
 * Campaigns, Deliverables, Reports, Organization, Help & Support). Each page states what the module covers, links to the
 * screens that already hold the underlying data, and offers the support contact. No figures are invented here.
 */
import { Link, useLocation } from 'react-router';
import { ActionTile, Card, CardHead, Heading, Text } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { Icon, type GlyphName } from '../../../shell/Icon';
import './modules.css';

export interface ModuleSpec {
  id: 'visibility' | 'crm' | 'campaigns' | 'deliverables' | 'reports' | 'organization' | 'help';
  icon: GlyphName;
  /** screens that already exist and hold this module's data */
  links: { to: string; icon: GlyphName; key: string }[];
}

export const MODULES: Record<string, ModuleSpec> = {
  '/visibility': { id: 'visibility', icon: 'seo', links: [
    { to: '/audit', icon: 'audit', key: 'audit' }, { to: '/integrations', icon: 'integrations', key: 'integrations' }, { to: '/performance', icon: 'reports', key: 'performance' },
  ] },
  '/crm': { id: 'crm', icon: 'crm', links: [
    { to: '/leads', icon: 'leads', key: 'leads' }, { to: '/inbox', icon: 'inbox', key: 'inbox' }, { to: '/approvals', icon: 'approvals', key: 'approvals' },
  ] },
  '/campaigns': { id: 'campaigns', icon: 'campaigns', links: [
    { to: '/ads', icon: 'ads', key: 'ads' }, { to: '/content', icon: 'content', key: 'content' }, { to: '/calendar', icon: 'calendar', key: 'calendar' },
  ] },
  '/deliverables': { id: 'deliverables', icon: 'deliverables', links: [
    { to: '/content', icon: 'content', key: 'content' }, { to: '/approvals', icon: 'approvals', key: 'approvals' }, { to: '/calendar', icon: 'calendar', key: 'calendar' },
  ] },
  '/reports': { id: 'reports', icon: 'reports', links: [
    { to: '/audit/dashboard', icon: 'audit', key: 'auditReports' }, { to: '/performance', icon: 'reports', key: 'performance' }, { to: '/leads', icon: 'leads', key: 'leads' },
  ] },
  '/organization': { id: 'organization', icon: 'organization', links: [
    { to: '/settings', icon: 'settings', key: 'settings' }, { to: '/team', icon: 'team', key: 'team' }, { to: '/billing', icon: 'billing', key: 'billing' }, { to: '/settings/security', icon: 'key', key: 'security' },
  ] },
  '/help': { id: 'help', icon: 'help', links: [
    { to: '/setup', icon: 'settings', key: 'setup' }, { to: '/notifications', icon: 'bell', key: 'notifications' }, { to: '/settings/security', icon: 'key', key: 'security' },
  ] },
};
export const MODULE_PATHS = Object.keys(MODULES) as (keyof typeof MODULES)[];

export function ModulePage({ path }: { path?: string }) {
  const { t } = useApp();
  const location = useLocation();
  const spec = MODULES[path ?? location.pathname] ?? MODULES['/help']!;
  const k = (s: string) => t(`shell.module.${spec.id}.${s}`);
  const bullets = [1, 2, 3].map((i) => k(`point${i}`));
  return (
    <section className="qm-page qm-module" aria-labelledby="page-title">
      <header className="qm-module__head">
        <span className="qm-icontile" aria-hidden="true"><Icon name={spec.icon} size={22} /></span>
        <div>
          <Heading level={1} id="page-title">{k('title')}</Heading>
          <Text tone="muted">{k('intro')}</Text>
        </div>
      </header>
      <div className="qm-module__grid">
        <Card aria-labelledby="module-scope">
          <CardHead id="module-scope" title={t('shell.module.scope')} />
          <ul className="qm-module__points">
            {bullets.map((b) => <li key={b}><Icon name="check-circle" size={18} /><span>{b}</span></li>)}
          </ul>
          <Text tone="muted">{t('shell.module.status')}</Text>
        </Card>
        <Card aria-labelledby="module-links">
          <CardHead id="module-links" title={t('shell.module.today')} sub={t('shell.module.todaySub')} />
          <div className="qm-module__links">
            {spec.links.map((l) => <ActionTile key={l.to} href={l.to} icon={<Icon name={l.icon} size={18} />} title={t(`shell.module.link.${l.key}`)} />)}
          </div>
        </Card>
        <Card aria-labelledby="module-support" className="qm-module__support">
          <CardHead id="module-support" title={t('shell.help.title')} icon={<Icon name="headset" size={16} />} />
          <Text>{t('shell.module.supportBody')}</Text>
          <div className="qm-row">
            <a className="qm-button" data-variant="ink" href="mailto:support@quixomarketing.com">{t('shell.module.email')}</a>
            <Link className="qm-button" to="/setup">{t('shell.module.setup')}</Link>
          </div>
        </Card>
      </div>
    </section>
  );
}
